(() => {
  const CHRISTMAS_GIFT_HANDLE = 'one-random-christmas-minifigure-free-gift';
  const LEGACY_GIFT_HANDLE = 'two-random-halloween-minifigures-free-gift';
  const CHRISTMAS_GIFT_THRESHOLD_USD = 1990;
  let giftSyncPromise;
  let giftSyncAgain = false;

  function formatCartMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || window.Shopify?.currency?.active || 'USD'
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${currency || 'USD'}`;
    }
  }

  function updateCartBadge(itemCount) {
    const drawer = document.querySelector('[data-cart-drawer]');
    document.querySelectorAll('.gm-cart-link').forEach(link => {
      let badge = link.querySelector('span');
      if (itemCount > 0 && !badge) {
        badge = document.createElement('span');
        link.append(badge);
      }
      if (badge) {
        badge.textContent = String(itemCount);
        if (itemCount === 0) badge.remove();
      }
      const itemLabel = itemCount === 1 ? drawer?.dataset.itemLabel : drawer?.dataset.itemsLabel;
      link.setAttribute('aria-label', `${drawer?.dataset.cartLabel || 'Cart with'} ${itemCount} ${itemLabel || (itemCount === 1 ? 'item' : 'items')}`);
    });
  }

  function createCartDrawerItem(item, drawer, currency) {
    const article = document.createElement('article');
    article.className = 'gm-cart-drawer__item';

    const imageLink = document.createElement('a');
    imageLink.className = 'gm-cart-drawer__image';
    imageLink.href = item.url;
    const imageUrl = item.featured_image?.url || item.image;
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = item.product_title || item.title;
      image.width = 164;
      image.height = 164;
      image.loading = 'lazy';
      imageLink.append(image);
    }

    const details = document.createElement('div');
    details.className = 'gm-cart-drawer__details';
    const title = document.createElement('a');
    title.className = 'gm-cart-drawer__title';
    title.href = item.url;
    title.textContent = item.product_title || item.title;
    details.append(title);
    if (item.variant_title && item.variant_title !== 'Default Title') {
      const variant = document.createElement('p');
      variant.textContent = item.variant_title;
      details.append(variant);
    }
    const quantity = document.createElement('p');
    quantity.textContent = `${drawer.dataset.quantityLabel}: ${item.quantity}`;
    details.append(quantity);
    const remove = document.createElement('a');
    remove.href = drawer.dataset.cartUrl;
    remove.dataset.cartDrawerRemove = item.key;
    remove.textContent = drawer.dataset.removeLabel;
    details.append(remove);

    const linePrice = document.createElement('strong');
    linePrice.textContent = item.final_line_price === 0 ? drawer.dataset.freeLabel : formatCartMoney(item.final_line_price, currency);
    article.append(imageLink, details, linePrice);
    return article;
  }

  function renderCartDrawer(cart) {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    const items = drawer.querySelector('[data-cart-drawer-items]');
    const footer = drawer.querySelector('[data-cart-drawer-footer]');
    const total = drawer.querySelector('[data-cart-drawer-total]');
    items.replaceChildren();
    if (cart.items.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'gm-cart-drawer__empty';
      empty.textContent = drawer.dataset.emptyLabel;
      items.append(empty);
      footer.hidden = true;
    } else {
      cart.items.forEach(item => items.append(createCartDrawerItem(item, drawer, cart.currency)));
      footer.hidden = false;
      total.textContent = formatCartMoney(cart.total_price, cart.currency);
    }
    updateCartBadge(cart.item_count);
  }

  async function refreshCartDrawer() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart could not be refreshed.');
    const cart = await response.json();
    renderCartDrawer(cart);
    return cart;
  }

  function openCartDrawer(message = '') {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    drawer.querySelector('[data-cart-drawer-status]').textContent = message;
    if (!drawer.open) drawer.showModal();
    drawer.querySelector('[data-cart-drawer-close]')?.focus();
  }

  async function giftCartRequest(path, data) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, data ? {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data)
    } : { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Christmas gift cart update failed.');
    return response.json();
  }

  function christmasThreshold(cart) {
    if (cart.currency === 'USD') return CHRISTMAS_GIFT_THRESHOLD_USD;
    const rate = Number(window.Shopify?.currency?.rate);
    if (cart.currency === window.Shopify?.currency?.active && Number.isFinite(rate) && rate > 0) {
      return Math.ceil(CHRISTMAS_GIFT_THRESHOLD_USD * rate);
    }
    return null;
  }

  async function reconcileChristmasGift() {
    let cart = await giftCartRequest('cart.js');
    let changed = false;
    const legacyLines = cart.items.filter(item => item.handle === LEGACY_GIFT_HANDLE);
    if (legacyLines.length) {
      cart = await giftCartRequest('cart/update.js', { updates: Object.fromEntries(legacyLines.map(item => [item.key, 0])) });
      changed = true;
    }
    const giftLines = cart.items.filter(item => item.handle === CHRISTMAS_GIFT_HANDLE);
    const excludedValue = cart.items.filter(item => item.handle === CHRISTMAS_GIFT_HANDLE || item.product_type === 'SEEL-WFP' || item.gift_card)
      .reduce((total, item) => total + item.final_line_price, 0);
    const qualifyingTotal = Math.max(0, cart.total_price - excludedValue);
    const threshold = christmasThreshold(cart);
    const qualifies = threshold !== null && qualifyingTotal >= threshold;
    const updates = {};
    giftLines.forEach((item, index) => {
      const desired = qualifies && index === 0 ? 1 : 0;
      if (item.quantity !== desired) updates[item.key] = desired;
    });
    if (Object.keys(updates).length) {
      cart = await giftCartRequest('cart/update.js', { updates });
      changed = true;
    }
    if (qualifies && giftLines.length === 0) {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${CHRISTMAS_GIFT_HANDLE}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Christmas gift product is unavailable.');
      const product = await response.json();
      const variant = product.variants?.find(item => item.available && Number(item.price) === 0);
      if (!variant) throw new Error('A free Christmas gift variant is unavailable.');
      await giftCartRequest('cart/add.js', { items: [{ id: variant.id, quantity: 1, properties: { _gm_christmas_gift: 'One random Christmas minifigure', _gm_gift_rule: 'US$19.90 merchandise subtotal after discounts; one per order' } }] });
      changed = true;
    }
    if (changed) document.dispatchEvent(new CustomEvent('gm:christmas-gift-updated', { detail: { qualifies } }));
    return { changed, qualifies, unsupportedCurrency: threshold === null };
  }

  async function syncChristmasGift() {
    if (giftSyncPromise) {
      giftSyncAgain = true;
      return giftSyncPromise;
    }
    giftSyncPromise = (async () => {
      let result;
      let changed = false;
      do {
        giftSyncAgain = false;
        result = await reconcileChristmasGift();
        changed = changed || result.changed;
      } while (giftSyncAgain);
      return { ...result, changed };
    })().catch(error => {
      console.warn('[GD Monster] Christmas gift sync skipped:', error.message);
      return { changed: false, error: true };
    }).finally(() => { giftSyncPromise = null; });
    return giftSyncPromise;
  }

  function init() {
    document.querySelectorAll('[data-product-section]').forEach(section => {
      if (section.dataset.ready) return;
      section.dataset.ready = 'true';
      const image = section.querySelector('.gm-pdp__image');
      const imageLink = section.querySelector('[data-full-image]');
      const thumbnails = section.querySelectorAll('[data-product-image]');
      thumbnails.forEach(button => button.addEventListener('click', () => {
        if (!image || !imageLink) return;
        image.removeAttribute('srcset');
        image.src = button.dataset.src;
        image.alt = button.dataset.alt;
        imageLink.href = button.dataset.full;
        thumbnails.forEach(thumb => thumb.setAttribute('aria-pressed', String(thumb === button)));
      }));
      const variants = section.querySelector('[data-variant-select]');
      variants?.addEventListener('change', () => {
        const option = variants.selectedOptions[0];
        const available = option.dataset.available === 'true';
        section.querySelector('[data-variant-price]').textContent = option.dataset.price;
        const compare = section.querySelector('[data-variant-compare]');
        compare.textContent = option.dataset.compare;
        compare.hidden = !option.dataset.compare;
        const status = section.querySelector('[data-variant-availability]');
        status.textContent = available ? 'Available to order' : 'Currently sold out';
        status.dataset.soldOut = String(!available);
        const add = section.querySelector('[data-add-button]');
        add.disabled = !available;
        add.textContent = available ? 'Add to cart' : 'Sold out';
        const buyNow = section.querySelector('[data-gm-buy-now]');
        if (buyNow) buyNow.disabled = !available;
        section.querySelector('[data-variant-sku]').textContent = option.dataset.sku || 'See product details';
        if (option.dataset.imageId) section.querySelector(`[data-product-image="${option.dataset.imageId}"]`)?.click();
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        history.replaceState({}, '', url);
      });
    });
    document.querySelectorAll('[data-spotlight]').forEach(section => {
      if (section.dataset.ready) return;
      section.dataset.ready = 'true';
      const buttons = section.querySelectorAll('[data-spotlight-select]');
      buttons.forEach(button => button.addEventListener('click', () => {
        section.querySelectorAll('[data-spotlight-panel]').forEach(panel => { panel.hidden = panel.dataset.spotlightPanel !== button.dataset.spotlightSelect; });
        buttons.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        section.querySelector('[data-spotlight-count]').textContent = `${String(Number(button.dataset.spotlightSelect) + 1).padStart(2, '0')} / 03`;
      }));
    });
    document.querySelectorAll('[data-spotlight-jump]').forEach(link => {
      if (link.dataset.ready) return;
      link.dataset.ready = 'true';
      link.addEventListener('click', () => document.querySelector(`[data-spotlight-select="${link.dataset.spotlightJump}"]`)?.click());
    });
    document.querySelectorAll('[data-curated]').forEach(collection => {
      if (collection.dataset.ready) return;
      collection.dataset.ready = 'true';
      const buttons = collection.querySelectorAll('[data-world-filter]');
      const cards = collection.querySelectorAll('[data-world]');
      collection.filterWorld = world => {
        let count = 0;
        cards.forEach(card => { card.hidden = world !== 'all' && card.dataset.world !== world; if (!card.hidden) count++; });
        buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.worldFilter === world)));
        collection.querySelector('.gm-filter-count').textContent = `${count} featured figure${count === 1 ? '' : 's'}`;
      };
      buttons.forEach(button => button.addEventListener('click', () => collection.filterWorld(button.dataset.worldFilter)));
    });
    document.querySelectorAll('[data-world-select]').forEach(link => {
      if (link.dataset.ready) return;
      link.dataset.ready = 'true';
      link.addEventListener('click', () => {
        const collection = document.querySelector('[data-curated]');
        if (collection && collection.filterWorld) collection.filterWorld(link.dataset.worldSelect);
      });
    });
    const rail = document.getElementById('gm-product-rail');
    if (rail && !rail.dataset.ready) {
      rail.dataset.ready = 'true';
      const controls = document.querySelectorAll('[data-rail-step]');
      const refresh = () => controls.forEach(button => {
        const previous = Number(button.dataset.railStep) < 0;
        button.disabled = previous ? rail.scrollLeft < 2 : rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
      });
      controls.forEach(button => button.addEventListener('click', () => rail.scrollBy({ left: Number(button.dataset.railStep) * rail.clientWidth * 0.82, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' })));
      rail.addEventListener('scroll', refresh, { passive: true });
      new ResizeObserver(refresh).observe(rail);
      refresh();
    }
    const menu = document.querySelector('.gm-mobile-menu');
    if (menu && !menu.dataset.ready) {
      menu.dataset.ready = 'true';
      document.addEventListener('keydown', event => { if (event.key === 'Escape' && menu.open) { menu.open = false; menu.querySelector('summary').focus(); } });
      document.addEventListener('click', event => { if (menu.open && !menu.contains(event.target)) menu.open = false; });
    }

    const drawer = document.querySelector('[data-cart-drawer]');
    if (drawer && !drawer.dataset.ready) {
      drawer.dataset.ready = 'true';
      drawer.querySelector('[data-cart-drawer-close]')?.addEventListener('click', () => drawer.close());
      drawer.addEventListener('click', async event => {
        if (event.target === drawer) {
          drawer.close();
          return;
        }
        const remove = event.target.closest('[data-cart-drawer-remove]');
        if (!remove) return;
        event.preventDefault();
        remove.setAttribute('aria-busy', 'true');
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: remove.dataset.cartDrawerRemove, quantity: 0 })
        });
        if (!response.ok) {
          remove.removeAttribute('aria-busy');
          drawer.querySelector('[data-cart-drawer-status]').textContent = drawer.dataset.errorLabel;
          return;
        }
        await syncChristmasGift();
        await refreshCartDrawer();
      });
      document.querySelectorAll('.gm-cart-link').forEach(link => link.addEventListener('click', async event => {
        event.preventDefault();
        try {
          await syncChristmasGift();
          await refreshCartDrawer();
          openCartDrawer();
        } catch (error) {
          window.location.href = link.href;
        }
      }));
    }

    document.querySelectorAll('[data-ajax-add-button]').forEach(button => {
      const form = button.form;
      if (!form || form.dataset.ajaxCartReady) return;
      form.dataset.ajaxCartReady = 'true';
      form.addEventListener('submit', async event => {
        const submitter = event.submitter;
        const buyNow = submitter?.matches('[data-gm-buy-now]');
        if (submitter !== button && !buyNow) return;
        event.preventDefault();
        if (form.dataset.ajaxCartBusy) return;
        form.dataset.ajaxCartBusy = 'true';
        const status = form.querySelector('[data-cart-form-status]');
        const controls = Array.from(form.querySelectorAll('[data-ajax-add-button], [data-gm-buy-now]'))
          .map(control => ({ control, disabled: control.disabled, markup: control.innerHTML }));
        controls.forEach(({control}) => { control.disabled = true; });
        submitter.setAttribute('aria-busy', 'true');
        submitter.textContent = buyNow ? 'Preparing checkout…' : (drawer?.dataset.addingLabel || 'Adding…');
        if (status) { status.hidden = true; status.textContent = ''; }
        let itemAdded = false;
        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form)
          });
          if (!response.ok) throw new Error('Product could not be added.');
          itemAdded = true;
          const giftResult = await syncChristmasGift();
          if (buyNow) {
            if (giftResult.error) throw new Error('Gift could not be checked.');
            window.location.assign(`${root}checkout`);
            return;
          }
          await refreshCartDrawer();
          openCartDrawer(drawer?.dataset.addedLabel || 'Added to your cart.');
        } catch (error) {
          if (status) {
            status.textContent = itemAdded
              ? 'Your item is in the cart. Please open your cart to continue; it has not been added again.'
              : (drawer?.dataset.errorLabel || 'Could not add this item. Please try again.');
            status.hidden = false;
          }
        } finally {
          controls.forEach(({control, disabled, markup}) => { control.innerHTML = markup; control.disabled = disabled; });
          submitter.removeAttribute('aria-busy');
          delete form.dataset.ajaxCartBusy;
        }
      });
    });

    if (!document.documentElement.dataset.gmGiftReady) {
      document.documentElement.dataset.gmGiftReady = 'true';
      syncChristmasGift().then(result => {
        if (result.changed && location.pathname.endsWith('/cart')) location.reload();
      });
      document.addEventListener('click', async event => {
        const checkout = event.target.closest('button[name="checkout"], input[name="checkout"]');
        if (!checkout) return;
        if (checkout.dataset.gmGiftCheckout === 'ready') {
          delete checkout.dataset.gmGiftCheckout;
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        checkout.disabled = true;
        const result = await syncChristmasGift();
        if (result.error) {
          checkout.disabled = false;
          const status = document.querySelector('[data-cart-drawer-status]');
          if (status) status.textContent = 'We could not check your gift. Please open your cart and try again.';
          return;
        }
        if (result.changed && location.pathname.endsWith('/cart')) {
          location.reload();
          return;
        }
        checkout.dataset.gmGiftCheckout = 'ready';
        checkout.disabled = false;
        checkout.click();
      }, true);
    }
  }
  init();
  document.addEventListener('shopify:section:load', init);
})();
